import React, { useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import { useTranslation } from "react-i18next";
import { useGetModuleEmployeesQuery } from "@redux/features/employees/employeesApi";
import { useGetModulePaymentTermsQuery } from "@redux/features/paymentTerms/paymentTermsApi";
import { useGetModuleDeliveryTermsQuery } from "@redux/features/deliveryTerms/deliveryTermsApi";
import { SaleOrdersFormTyping } from "../../types";
import { useGetModuleCustomerQuery } from "@redux/features/customers/customersApi";
import CreateOurReferenceModal from "./CreateOurReferenceModal";
import CreateTheirReferenceModal from "./CreateTheirReferenceModal";
import TheirReferenceField from "./TheirReferenceField";

export default function ReferencesSection() {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general" });

    const { control, setValue } = useFormContext<SaleOrdersFormTyping>();

    // === Modals ===
    const [isNewOurReferenceModalOpen, setIsNewOurReferenceModalOpen] = useState<boolean>(false);
    const [isNewTheirReferenceModalOpen, setIsNewTheirReferenceModalOpen] = useState<boolean>(false);

    // === RTKQ ===
    // --> Our references (obtained from our employees)
    const { data: ourReferencesOptions, isLoading: isOurReferenceOptionsLoading } = useGetModuleEmployeesQuery("sale");

    // --> Their references (obtained from the selected supplier)
    const customer = useWatch({
        control,
        name: "customer"
    });

    const isInvoiced = useWatch({
        control,
        name: "is_invoiced"
    });

    const mode = useWatch({
        control,
        name: "mode"
    });

    const { data: extendedCustomerData, isLoading: isExtendedCustomerDataLoading } = useGetModuleCustomerQuery(
        { module: "sale", id: customer?.id as number },
        { skip: !customer }
    );
    const activeCustomerData =
        extendedCustomerData?.id === customer?.id ? extendedCustomerData : undefined;

    useEffect(() => {
        if (activeCustomerData && mode === "creation") {
            !!activeCustomerData.payment_terms &&
                setValue("payment_terms", activeCustomerData.payment_terms);
            !!activeCustomerData.delivery_terms &&
                setValue("delivery_terms", activeCustomerData.delivery_terms);
        }
    }, [activeCustomerData, mode, setValue]);

    // --> Payment terms
    const { data: paymentTermsOptions, isLoading: paymentTermsOptionsLoading } = useGetModulePaymentTermsQuery("sale");

    // --> Delivery terms
    const {
        data: deliveryTermsOptions,
        isLoading: deliveryTermsOptionsLoading
    } = useGetModuleDeliveryTermsQuery("sale");

    return (
        <>
            <BaseInputsGrid title={t("categories.references")}>
                {/* Our references */}
                <Controller
                    name="our_reference"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseDropdown
                            {...field}
                            {...fieldState}

                            label={t("dropdowns.ourReference.label")}
                            placeholder={t("dropdowns.ourReference.placeholder")}
                            options={ourReferencesOptions}
                            getter={{
                                label: (opt) => opt.name,
                                key: (opt) => opt.id
                            }}

                            disabled={isInvoiced}

                            autocomplete

                            action={{
                                title: t("dropdowns.ourReference.buttons.create"),
                                onClick: () => setIsNewOurReferenceModalOpen(true)
                            }}
                            isLoading={isOurReferenceOptionsLoading || isExtendedCustomerDataLoading}

                            autofill

                            rememberLastValueByKey={"sale-order.our-references"}
                        />
                    )}
                />

                {/* Their references */}
                <TheirReferenceField
                    customer={customer}
                    customerData={activeCustomerData}
                    isLoading={isExtendedCustomerDataLoading}
                    mode={mode}
                    onCreate={() => setIsNewTheirReferenceModalOpen(true)}
                />

                {/* Payment terms */}
                <Controller
                    name="payment_terms"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseDropdown
                            {...field}
                            {...fieldState}

                            label={t("dropdowns.paymentTerms.label")}
                            placeholder={t("dropdowns.paymentTerms.placeholder")}
                            options={paymentTermsOptions}
                            getter={{
                                label: (opt) => opt.name,
                                key: (opt) => opt.id
                            }}

                            disabled={isInvoiced}

                            autocomplete
                            isLoading={paymentTermsOptionsLoading || isExtendedCustomerDataLoading}

                            autofill
                        />
                    )}
                />

                {/* Delivery terms */}
                <Controller
                    name="delivery_terms"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseDropdown
                            {...field}
                            {...fieldState}

                            label={t("dropdowns.deliveryTerms.label")}
                            placeholder={t("dropdowns.deliveryTerms.placeholder")}
                            options={deliveryTermsOptions}
                            getter={{
                                label: (opt) => opt.name,
                                key: (opt) => opt.id
                            }}

                            disabled={isInvoiced}

                            autocomplete
                            isLoading={deliveryTermsOptionsLoading || isExtendedCustomerDataLoading}

                            autofill
                        />
                    )}
                />
            </BaseInputsGrid>

            <CreateOurReferenceModal
                isOpen={isNewOurReferenceModalOpen}
                onClose={() => setIsNewOurReferenceModalOpen(false)}
                onCreate={ourRef => {
                    setValue("our_reference", {
                        id: ourRef?.id,
                        name: ourRef?.name
                    });
                    setIsNewOurReferenceModalOpen(false);
                }}
            />

            <CreateTheirReferenceModal
                customer={activeCustomerData}
                isOpen={isNewTheirReferenceModalOpen}
                onClose={() => setIsNewTheirReferenceModalOpen(false)}
                onCreate={theirRef => {
                    setValue("their_reference", {
                        id: theirRef.id,
                        name: theirRef.name
                    });
                    setValue("their_reference_name", null);
                    setIsNewTheirReferenceModalOpen(false);
                }}
            />
        </>
    );
}
